// src/pages/ViewCustomerRooms.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";

// Import Services - Only what we need (no feeter services)
import { getCustomerById, updateCustomer } from "../services/customerService";
import { getAllInvoicesOfCustomer } from "../services/invoiceService";
import { createCalendar, getCalendarsByJob } from "../services/calendarService";
// Top-level jobs/{id} holds the driver-assignment fields
// (assignedDriverIds + directDriverIds). The embedded customer.jobs[]
// copy doesn't have them, so we have to fetch the top-level doc and
// merge the two fields into local state on load.
import { getJob as getTopLevelJob } from "../services/jobService";
// Direct Firestore primitives for the rooms dual-write — see comments
// at each call site. Rooms are the only field that's actively written
// from BOTH the admin app and the fitter portal, so the embedded copy
// in customer.jobs[].rooms can fall out of sync with jobs/{id}.rooms.
// Until the admin app fully reads/writes from top-level (later sprint),
// each admin save mirrors the new rooms array to jobs/{id}.rooms.
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";

// Import Components - Clean, modern components
import CustomerHeader from "../components/customers/CustomerHeader";
import JobAddressCard from "../components/customers/JobAddressCard";
import AssignedDriversBar from "../components/customers/AssignedDriversBar";
import JobDetailsCard from "../components/customers/JobDetailsCard";
import RoomsTable from "../components/customers/RoomsTable";
import RoomModal from "../components/customers/RoomModal";
import CalendarEventModal from "../components/customers/CalendarEventModal";
import InvoicesPopover from "../components/customers/InvoicesPopover";
import CalendarEventsPopover from "../components/customers/CalendarEventsPopover";

import JobModal from "../components/customers/JobModal";
// Import Utilities
import parseStringDate from "../utils/parseStringDate";

/**
 * Address fields for the rooms dual-write.
 *
 * The mirror writes with { merge: true } and, per the comments at each call
 * site, CREATES jobs/{id} when no top-level doc exists yet. A doc born that
 * way used to carry only customerId/rooms/updatedAt — never address or
 * postcode — so the fitter portal had nothing to put on the job card.
 *
 * Only include a key when the local value is a non-empty string, so a merge
 * can never overwrite good data with "". Same defensive shape as the
 * `if (customer)` guard in seedTopLevelJob.
 *
 * @param {Object} job - The job currently in scope.
 * @returns {{address?: string, postcode?: string}}
 */
const addressFieldsFor = (job) => {
  const fields = {};
  if (typeof job?.address === "string" && job.address.trim()) {
    fields.address = job.address;
  }
  if (typeof job?.postcode === "string" && job.postcode.trim()) {
    fields.postcode = job.postcode;
  }
  return fields;
};

/**
 * ViewCustomerRooms Component
 *
 * Main page for managing customer job areas/rooms with measurements.
 * This is the central hub for room management, providing a clean and intuitive
 * interface for adding, editing, and organizing measurement areas.
 *
 * Key Features:
 * ✅ Customer and job information display with clean layout
 * ✅ Room/area management with full CRUD operations
 * ✅ Professional image gallery with drag-and-drop upload
 * ✅ Calendar event creation for appointments
 * ✅ Invoice management integration with popover interface
 * ✅ Advanced search and filtering capabilities
 * ✅ Responsive design optimized for all screen sizes
 * ✅ Comprehensive error handling and user feedback
 * ✅ Loading states with professional spinners
 * ✅ Breadcrumb navigation for better UX
 *
 * @returns {JSX.Element} The complete room management interface
 */
const ViewCustomerRooms = () => {
  // Get URL parameters for customer and job identification
  const { id: customerId, jobId } = useParams();
  const navigate = useNavigate();
  const [jobEditTrigger, setJobEditTrigger] = useState(null);
  const [isJobEditModalOpen, setIsJobEditModalOpen] = useState(false);
  // Redirect to customers page if missing required parameters
  useEffect(() => {
    if (!customerId || !jobId) {
      navigate("/customers");
    }
  }, [customerId, jobId, navigate]);
  const [jobParams, setJobParams] = useState({
    id: null,
    date: new Date().toLocaleDateString("en-GB"),
    hasUnderlay: false,
    hasGrippers: false,
    floorIsGood: false,
    oldFlooringRemoved: false,
    furnitureRemoval: false,
    concrete: false,
    doorsNeedCutting: 0,
    numberOfDoorPlateNeeded: 0,
    comments: "",
    plan: "",
    products: "",
  });
  // Add this function to your ViewCustomerRooms component, around line 200-250
  // (after your existing functions but before the render return)

  /**
   * Handle customer data updates from CustomerHeader
   * Updates the local customer state when customer is edited
   */
  const handleCustomerUpdate = (updatedCustomer) => {
    setCustomer(updatedCustomer);

    // Optional: Show success message
    showMessage("Customer information updated successfully", "success");
  };

  // Update your handleEditJob function
  const handleEditJob = () => {
    // Initialize the form with current job data
    if (job) {
      setJobParams(JSON.parse(JSON.stringify(job)));
    }

    setIsJobEditModalOpen(true);
  };

  const handleJobInputChange = (e) => {
    if (!e.target) {
      if (typeof e === "string") {
        if (e === "") return;
        setJobParams({ ...jobParams, date: e });
      } else {
        setJobParams({ ...jobParams, date: e.value });
      }
      return;
    }

    const { value, id, type, checked } = e.target;

    if (type === "number") {
      if (value === "") {
        setJobParams({ ...jobParams, [id]: 0 });
      } else {
        setJobParams({ ...jobParams, [id]: Number(value) });
      }
    } else if (type === "checkbox") {
      setJobParams({ ...jobParams, [id]: checked });
    } else {
      setJobParams({ ...jobParams, [id]: value });
    }
  };

  const saveJobFromModal = async () => {
    if (!jobParams.date) {
      showMessage("Please enter a Date", "error");
      return;
    }

    try {
      // Get current customer data to update the job
      const customerData = await getCustomerById(customerId);

      if (!customerData) {
        showMessage("Customer not found", "error");
        return;
      }

      // Update the specific job in the jobs array
      const updatedJobs = customerData.jobs.map((j) =>
        j.id === jobParams.id ? jobParams : j,
      );

      // Update customer with modified jobs
      await updateCustomer(customerId, {
        ...customerData,
        jobs: updatedJobs,
      });

      // Update local state
      setJob(jobParams);

      // Update customer jobs array
      if (customer && customer.jobs) {
        const updatedCustomer = {
          ...customer,
          jobs: updatedJobs,
        };
        setCustomer(updatedCustomer);
      }

      setIsJobEditModalOpen(false);
      showMessage("Job updated successfully", "success");
    } catch (error) {
      console.error("Error updating job:", error);
      showMessage("Failed to update job. Please try again.", "error");
    }
  };

  // ==========================================
  // STATE MANAGEMENT - Organized by concern
  // ==========================================

  // Main data state - Core business entities
  const [customer, setCustomer] = useState({});
  const [job, setJob] = useState({});
  const [roomsList, setRoomsList] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);

  // UI state - Interface controls and filtering
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Modal state - Controls for different modal dialogs
  const [addRoomModal, setAddRoomModal] = useState(false);
  const [addCalendarModal, setAddCalendarModal] = useState(false);

  // ==========================================
  // FORM STATE - Room Management
  // ==========================================

  // Default parameters for room creation/editing
  // This ensures consistent data structure across the application
  const [defaultRoomParams] = useState({
    id: null,
    name: "",
    comments: "",
    // Per-selection text colouring for the note. Array of
    // { text, color: 'red'|'green'|'orange'|null }; joins back to `comments`
    // exactly. Empty = plain note, same as every room before this feature.
    // Contract: src/utils/coloredText.js
    commentsSegments: [],
    images: [], // Array to store room images with metadata
  });

  // Current room form data - used for both add and edit operations
  const [roomParams, setRoomParams] = useState(
    JSON.parse(JSON.stringify(defaultRoomParams)),
  );

  // ==========================================
  // CALENDAR EVENT STATE - Appointment Management
  // ==========================================

  /**
   * Helper function to create Date objects at specific times
   * Used for setting default appointment times (8 AM, 9 AM)
   */
  const getTodayAt = (hours, minutes) => {
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Calendar date/time management with sensible defaults
  const [minStartDate, setMinStartDate] = useState(getTodayAt(8, 0));
  const [minEndDate, setMinEndDate] = useState(getTodayAt(9, 0));
  const [eventType, setEventType] = useState(""); // 'measurement', 'installation', 'price'

  // Calendar form parameters with proper initialization.
  // `title` and `location` are pre-filled by handleAddToCalendar from job
  // context (customer name + job date for title; job address + postcode for
  // location). Both remain editable in the modal.
  const [calendarParams, setCalendarParams] = useState({
    title: "",
    location: "",
    start: "",
    end: "",
    description: "",
  });

  // ==========================================
  // DATA LOADING - Initial Data Fetch
  // ==========================================

  /**
   * Primary data loading effect
   *
   * Fetches all necessary data when component mounts or parameters change.
   * Implements proper error handling and loading states for better UX.
   *
   * Data Loading Sequence:
   * 1. Customer data with jobs array
   * 2. Specific job data with rooms array
   * 3. Invoice data for this customer
   * 4. Set loading state appropriately
   */
  useEffect(() => {
    // Only proceed if we have the required parameters
    if (customerId && jobId) {
      const fetchData = async () => {
        setLoading(true);

        try {
          // Step 1: Fetch customer data with embedded jobs
          const customerData = await getCustomerById(customerId);

          if (customerData) {
            setCustomer(customerData);

            // Step 2: Find and set the specific job data
            const jobData = customerData.jobs?.find((j) => j.id === jobId);

            if (jobData) {
              // Step 2a: Augment with driver-assignment fields from the
              // top-level jobs/{id} doc. These live there, not in the
              // embedded customer.jobs[] copy. Failure is non-fatal —
              // the page renders without driver pills if this read fails.
              try {
                const topLevel = await getTopLevelJob(customerId, jobId);
                if (topLevel) {
                  jobData.assignedDriverIds = topLevel.assignedDriverIds || [];
                  jobData.directDriverIds = topLevel.directDriverIds || [];
                  // Also pull through the denormalised customerName /
                  // customerPhone so the AssignedDriversBar header can
                  // show them (currently only used downstream).
                  if (topLevel.customerName)
                    jobData.customerName = topLevel.customerName;
                  if (topLevel.customerPhone)
                    jobData.customerPhone = topLevel.customerPhone;
                  // CRITICAL: rooms are written by BOTH the admin and the
                  // fitter portal. The fitter writes only to jobs/{id}.rooms;
                  // the embedded copy doesn't pick those up. So top-level
                  // is the source of truth for the current rooms list.
                  if (Array.isArray(topLevel.rooms)) {
                    jobData.rooms = topLevel.rooms;
                  }
                }
              } catch (e) {
                console.warn("Could not load top-level driver assignments:", e);
              }

              setJob(jobData);

              // Step 3: Initialize rooms data with empty array fallback
              if (jobData.rooms && Array.isArray(jobData.rooms)) {
                setRoomsList(jobData.rooms);
              } else {
                setRoomsList([]);
              }
            } else {
              // Job not found - show error and redirect
              showMessage("Job not found", "error");
              navigate(-1);
              return;
            }
          } else {
            // Customer not found - show error and redirect to customers page
            showMessage("Customer not found", "error");
            navigate("/customers");
            return;
          }

          // Step 4: Fetch invoice data for this customer
          const invoiceData = await getAllInvoicesOfCustomer(customerId);
          if (invoiceData && invoiceData.length > 0) {
          }
          if (invoiceData) {
            setInvoices(invoiceData);
          }

          // Step 5: Fetch calendar events for this job (non-blocking)
          try {
            const calendarData = await getCalendarsByJob(jobId);
            if (calendarData) {
              setCalendarEvents(calendarData);
            }
          } catch (calendarError) {
            // Calendar fetch failed, but don't break the page
            console.error("Error fetching calendar events:", calendarError);
            setCalendarEvents([]);
          }
        } catch (error) {
          // Comprehensive error handling with user feedback
          console.error("Error fetching data:", error);
          showMessage("Failed to load data. Please try again.", "error");

          // Optionally redirect on critical errors
          // navigate('/customers');
        } finally {
          // Always set loading to false, regardless of success/failure
          setLoading(false);
        }
      };

      // Execute the data fetching
      fetchData();
    }
  }, [customerId, jobId, navigate]);

  /**
   * Handle job data updates from JobDetailsCard
   * Updates the local job state when job is edited
   */
  const handleJobUpdate = (updatedJob) => {
    setJob(updatedJob);

    // Also update the customer's jobs array if needed
    if (customer && customer.jobs) {
      const updatedCustomer = {
        ...customer,
        jobs: customer.jobs.map((j) =>
          j.id === updatedJob.id ? updatedJob : j,
        ),
      };
      setCustomer(updatedCustomer);
    }

    // Show success message
    showMessage("Job information updated successfully", "success");
  };

  // ==========================================
  // TO DO LIST TOGGLE
  // ==========================================

  /**
   * Toggle customer's To Do list status
   * Switches between adding/removing customer from To Do list
   */
  const handleToggleTodoList = async () => {
    if (!customer || !customer.id) {
      showMessage("Customer data not loaded", "error");
      return;
    }

    const newTodoStatus = !customer.addToTodoList;

    try {
      // Update customer in Firebase
      await updateCustomer(customer.id, {
        ...customer,
        addToTodoList: newTodoStatus,
      });

      // Update local state
      setCustomer({
        ...customer,
        addToTodoList: newTodoStatus,
      });

      // Show success message
      showMessage(
        newTodoStatus
          ? "Customer added to To Do list"
          : "Customer removed from To Do list",
        "success",
      );
    } catch (error) {
      console.error("Error updating To Do status:", error);
      showMessage("Failed to update To Do list status", "error");
    }
  };

  // ==========================================
  // SEARCH AND FILTERING - Room List Management
  // ==========================================

  /**
   * Real-time search filtering effect
   *
   * Filters rooms based on search term, checking both name and comments.
   * Implements case-insensitive search for better user experience.
   */
  useEffect(() => {
    if (search.length > 0) {
      // Filter rooms by name and comments (case-insensitive)
      const filtered = roomsList.filter(
        (room) =>
          room.name.toLowerCase().includes(search.toLowerCase()) ||
          (room.comments &&
            room.comments.toLowerCase().includes(search.toLowerCase())),
      );

      setFilteredRooms(filtered);
    } else {
      // No search term - show all rooms
      setFilteredRooms(roomsList);
    }
  }, [search, roomsList]);

  // ==========================================
  // ROOM MANAGEMENT - CRUD Operations
  // ==========================================

  /**
   * Handle room form input changes
   *
   * Updates the room form state when user types in form fields.
   * Ensures controlled components for better form management.
   */
  const handleRoomInputChange = (e) => {
    const { value, id, segments } = e.target;

    // `segments` only ever arrives from ColoredCommentsEditor, alongside the
    // plain `comments` value it derives from. Every other field behaves as
    // before. Functional update so the two writes can't clobber each other.
    setRoomParams((prev) => ({
      ...prev,
      [id]: value,
      ...(segments !== undefined ? { commentsSegments: segments } : {}),
    }));
  };

  /**
   * Save room data (handles both create and update operations)
   *
   * Determines whether to create a new room or update existing based on room ID.
   * Implements proper validation, error handling, and user feedback.
   */
  const saveRoom = async () => {
    // Validation: Ensure room name is provided
    if (!roomParams.name.trim()) {
      showMessage("Please enter a room name", "error");
      return;
    }

    try {
      if (roomParams.id) {
        // UPDATE EXISTING ROOM
        // Update the room in the rooms array
        // Normalise the two note fields explicitly: an old room opened and
        // saved without touching the note has no `commentsSegments` key at
        // all, and Firestore rejects an explicit `undefined`.
        const updatedRooms = roomsList.map((room) =>
          room.id === roomParams.id
            ? {
                ...roomParams,
                comments: roomParams.comments || "",
                commentsSegments: roomParams.commentsSegments || [],
              }
            : room,
        );

        // Update the job with the new rooms array
        const updatedJob = { ...job, rooms: updatedRooms };

        // Update the customer with the modified job
        const updatedCustomer = {
          ...customer,
          jobs: customer.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
        };

        // Save to backend
        await updateCustomer(customerId, updatedCustomer);
        // Mirror to top-level jobs/{id}.rooms so the fitter portal
        // sees the latest. Transitional dual-write — remove once the
        // admin app fully reads/writes via top-level.
        // setDoc + merge (not updateDoc): jobs created after the Sprint-4
        // migration have no top-level jobs/{id} doc yet, and updateDoc throws
        // "No document to update" when it's missing. setDoc + merge creates it
        // (seeded with customerId) or updates it in place — old jobs unaffected.
        // address/postcode ride along so a doc created here isn't addressless
        // in the fitter portal — see addressFieldsFor.
        await setDoc(
          doc(db, "jobs", jobId),
          {
            customerId,
            rooms: updatedRooms,
            ...addressFieldsFor(job),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        // Update local state
        setJob(updatedJob);
        setRoomsList(updatedRooms);

        showMessage("Room updated successfully", "success");
      } else {
        // CREATE NEW ROOM
        // Create new room object with unique ID
        const newRoom = {
          id: uuidv4(),
          name: roomParams.name.trim(),
          comments: roomParams.comments || "",
          // Colouring for the note above. [] = plain. See coloredText.js.
          commentsSegments: roomParams.commentsSegments || [],
          images: [], // Start with empty images array
        };

        // Add to existing rooms
        const updatedRooms = [...roomsList, newRoom];

        // Update job and customer objects
        const updatedJob = { ...job, rooms: updatedRooms };
        const updatedCustomer = {
          ...customer,
          jobs: customer.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
        };

        // Save to backend
        await updateCustomer(customerId, updatedCustomer);
        // Mirror to top-level jobs/{id}.rooms so the fitter portal
        // sees the latest. Transitional dual-write.
        // setDoc + merge (not updateDoc): jobs created after the Sprint-4
        // migration have no top-level jobs/{id} doc yet, and updateDoc throws
        // "No document to update" when it's missing. setDoc + merge creates it
        // (seeded with customerId) or updates it in place — old jobs unaffected.
        // address/postcode ride along so a doc created here isn't addressless
        // in the fitter portal — see addressFieldsFor.
        await setDoc(
          doc(db, "jobs", jobId),
          {
            customerId,
            rooms: updatedRooms,
            ...addressFieldsFor(job),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        // Update local state
        setJob(updatedJob);
        setRoomsList(updatedRooms);

        showMessage("Room added successfully", "success");
      }

      // Close modal and reset form
      setAddRoomModal(false);
      setRoomParams(JSON.parse(JSON.stringify(defaultRoomParams)));
    } catch (error) {
      // Handle save errors with user feedback
      console.error("Error saving room:", error);
      showMessage("Failed to save room. Please try again.", "error");
    }
  };

  /**
   * Open room editing modal
   *
   * Prepares the form for either creating a new room or editing an existing one.
   * Handles form initialization with appropriate default values.
   *
   * @param {Object|null} room - Room object to edit, or null for new room
   */
  const editRoom = (room = null) => {
    if (room) {
      // EDIT MODE: Pre-populate form with existing room data.
      // Rooms written before this feature (and rooms added by the fitter app)
      // have no `commentsSegments` — default it so the editor gets a real
      // array and the save path never writes `undefined`.
      setRoomParams({
        ...room,
        comments: room.comments || "",
        commentsSegments: Array.isArray(room.commentsSegments)
          ? room.commentsSegments
          : [],
      });
    } else {
      // CREATE MODE: Initialize with empty form
      setRoomParams(JSON.parse(JSON.stringify(defaultRoomParams)));
    }

    // Open the modal
    setAddRoomModal(true);
  };

  /**
   * Delete room with user confirmation
   *
   * Implements safe deletion with confirmation dialog and proper error handling.
   * Updates both backend data and local state consistently.
   *
   * @param {Object} room - Room object to delete
   */
  const deleteRoom = async (room) => {
    if (!room) {
      return;
    }

    // Show confirmation dialog with room name for clarity
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${room.name}"? This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626", // Red color for destructive action
      cancelButtonColor: "#6b7280", // Gray color for cancel
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    // Proceed only if user confirmed
    if (result.isConfirmed) {
      try {
        // Remove room from rooms array
        const updatedRooms = roomsList.filter((r) => r.id !== room.id);

        // Update job and customer objects
        const updatedJob = { ...job, rooms: updatedRooms };
        const updatedCustomer = {
          ...customer,
          jobs: customer.jobs.map((j) => (j.id === jobId ? updatedJob : j)),
        };

        // Save to backend
        await updateCustomer(customerId, updatedCustomer);
        // Mirror to top-level jobs/{id}.rooms so the fitter portal
        // sees the deletion. Transitional dual-write.
        // setDoc + merge (not updateDoc): jobs created after the Sprint-4
        // migration have no top-level jobs/{id} doc yet, and updateDoc throws
        // "No document to update" when it's missing. setDoc + merge creates it
        // (seeded with customerId) or updates it in place — old jobs unaffected.
        // address/postcode ride along so a doc created here isn't addressless
        // in the fitter portal — see addressFieldsFor.
        await setDoc(
          doc(db, "jobs", jobId),
          {
            customerId,
            rooms: updatedRooms,
            ...addressFieldsFor(job),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        // Update local state
        setJob(updatedJob);
        setRoomsList(updatedRooms);

        showMessage("Room deleted successfully", "success");
      } catch (error) {
        // Handle deletion errors
        console.error("Error deleting room:", error);
        showMessage("Failed to delete room. Please try again.", "error");
      }
    }
  };

  // ==========================================
  // CALENDAR EVENT MANAGEMENT
  // ==========================================

  /**
   * Handle calendar form parameter changes
   *
   * Updates calendar form state when user modifies event details.
   */
  const handleCalendarParamChange = (e) => {
    const { id, value } = e.target;
    // Functional setState — when the modal fires two consecutive updates
    // (start + auto-derived end), the 2nd call sees the 1st call's update.
    // Without this, the 2nd update spreads stale state and silently
    // overwrites the 1st — causing the date picker to look "stuck".
    setCalendarParams((prev) => ({ ...prev, [id]: value }));
  };

  /**
   * Handle event type selection
   *
   * Updates the selected event type for calendar events.
   * Event types: 'measurement', 'installation', 'price'
   *
   * @param {string} type - The selected event type
   */
  const handleEventTypeChange = (type) => {
    setEventType(type);
  };

  /**
   * Handle start date/time change for calendar events
   *
   * Updates start date and automatically adjusts minimum end date.
   * Formats date for proper display and backend storage.
   *
   * @param {Date} date - Selected start date
   */
  const handleStartDateChange = (date) => {
    setMinStartDate(date);

    // Calculate end date as 1 hour after start date
    const endDate = new Date(date.getTime() + 60 * 60 * 1000); // Add 1 hour in milliseconds
    setMinEndDate(endDate);

    // Format start date for display and storage
    const formattedStartDate = date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Use 12-hour format for user friendliness
    });

    // Format end date (1 hour after start)
    const formattedEndDate = endDate.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    setCalendarParams({
      ...calendarParams,
      start: formattedStartDate,
      end: formattedEndDate, // Auto-set end date to 1 hour after start
    });
  };

  /**
   * Handle end date/time change for calendar events
   *
   * Updates end date with proper validation and formatting.
   *
   * @param {Date} date - Selected end date
   */
  const handleEndDateChange = (date) => {
    setMinEndDate(date);

    // Format date consistently with start date
    const formattedDate = date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    setCalendarParams({
      ...calendarParams,
      end: formattedDate,
    });
  };

  /**
   * Save calendar event to system
   *
   * Creates a new calendar event with comprehensive error handling.
   * Prevents duplicate events and provides clear user feedback.
   */
  const saveCalendarEvent = async () => {
    // Validation: Ensure event type is selected
    if (!eventType) {
      showMessage(
        "Please select an event type (Measurement, Installation, or Pricing)",
        "error",
      );
      return;
    }

    try {
      // Create comprehensive event description with customer details
      const eventDescription =
        calendarParams.description ||
        `${
          eventType.charAt(0).toUpperCase() + eventType.slice(1)
        } appointment for ${customer.name}

Customer Details:
• Location: ${customer.location || "Not specified"}
• Phone: ${customer.phone || "Not specified"}
• Post Code: ${customer.postCode || "Not specified"}
• Email: ${customer.email || "Not specified"}

Job Information:
• Job Date: ${job.date}
• Job ID: ${jobId}`;

      // Create new calendar event object — uses user-entered values from the
      // modal where provided, with sensible fallbacks if the user cleared a
      // pre-filled field (title) or left location empty.
      const newCalendarEvent = {
        title: calendarParams.title || `${customer.name} | Job ${job.date}`,
        description: eventDescription,
        location: calendarParams.location || "",
        jobId: jobId,
        customerId: customerId,
        start:
          calendarParams.start ||
          parseStringDate(job.date).toLocaleDateString("en-GB"),
        end:
          calendarParams.end ||
          parseStringDate(job.date).toLocaleDateString("en-GB"),
        eventType: eventType,
        // Default sortOrder so new events sit at the end of their day. Manual
        // reorders later overwrite this with 0,1,2,... — Date.now() is far
        // larger than those indices, which guarantees the new-event-first
        // semantics without re-numbering existing rows.
        sortOrder: Date.now(),
      };

      // Save to backend
      await createCalendar(newCalendarEvent);

      showMessage("Calendar event created successfully", "success");

      // Refresh calendar events list (non-blocking)
      try {
        const updatedCalendarData = await getCalendarsByJob(jobId);
        if (updatedCalendarData) {
          setCalendarEvents(updatedCalendarData);
        }
      } catch (refreshError) {
        console.error("Error refreshing calendar events:", refreshError);
        // Event was created successfully, just refresh failed
      }

      // Reset form and close modal
      setAddCalendarModal(false);
      setCalendarParams({
        title: "",
        location: "",
        start: "",
        end: "",
        description: "",
      });
      setEventType("");
      setMinStartDate(getTodayAt(8, 0));
      setMinEndDate(getTodayAt(9, 0));
    } catch (error) {
      // Handle calendar creation errors
      console.error("Error creating calendar event:", error);
      showMessage(
        "Failed to create calendar event. Please try again.",
        "error",
      );
    }
  };

  /**
   * Open calendar modal with proper initialization
   *
   * Prepares the calendar form with default values and opens the modal.
   */
  const handleAddToCalendar = () => {
    // Reset form to default values
    setMinStartDate(getTodayAt(8, 0));
    setMinEndDate(getTodayAt(9, 0));
    setEventType("");

    // Pre-fill title + location from job context so the modal opens with
    // sensible defaults the user can tweak. Both fields remain editable.
    const defaultTitle =
      customer?.name && job?.date ? `${customer.name} | Job ${job.date}` : "";
    const defaultLocation = [job?.address, job?.postcode]
      .filter(Boolean)
      .join(", ");

    setCalendarParams({
      title: defaultTitle,
      location: defaultLocation,
      start: "",
      end: "",
      description: "",
    });

    // Open modal
    setAddCalendarModal(true);
  };

  // ==========================================
  // USER FEEDBACK - Toast Messages
  // ==========================================

  /**
   * Display toast notification to user
   *
   * Provides consistent user feedback across the application using SweetAlert2.
   * Positioned at top-end for non-intrusive notification experience.
   *
   * @param {string} msg - Message to display
   * @param {string} type - Type of message ('success', 'error', 'warning', 'info')
   */
  const showMessage = (msg = "", type = "success") => {
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { container: "toast" },
    }).fire({
      icon: type,
      title: msg,
      padding: "10px 20px",
    });
  };

  // ==========================================
  // LOADING STATE - Show Loading Spinner
  // ==========================================

  // Show professional loading spinner while data is being fetched
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-96">
          {/* Professional loading spinner with smooth animation */}
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            <p className="mt-4 text-gray-600 text-lg">
              Loading room details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // MAIN RENDER - Component Layout
  // ==========================================

  return (
    <div className="container mx-auto px-4 py-6 bg-gray-50 min-h-screen">
      {/* Customer Information Header */}
      {/* Clean, professional header showing customer contact details */}
      <CustomerHeader
        customer={customer}
        onCustomerUpdate={handleCustomerUpdate}
      />

      {/* Job Address — mirrors CustomerHeader style, read-only.
          To change the address, use the "Edit Job" button (which opens JobModal
          where address + postcode are editable fields). */}
      <JobAddressCard job={job} />

      {/* Main Areas Management Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Section Header with Edit Job in top right */}
        <div className="flex justify-between items-start mb-4">
          {/* Section Title */}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              {/* Room icon for visual clarity */}
              <svg
                className="w-7 h-7 mr-3 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              Areas & Measurements
            </h2>
          </div>
        </div>

        {/* Description and Action Buttons on Left */}
        <div className="mb-6">
          <p className="text-gray-600 text-sm mb-4">
            Manage measurement areas, add notes, and track progress for this job
          </p>

          {/* Assigned drivers row — direct admin assignments live in
              jobs/{id}.directDriverIds; event-derived assignments live in
              assignedDriverIds. Either grants Firestore read + rooms-write
              access. See AssignedDriversBar header docstring. */}
          <AssignedDriversBar
            job={job}
            onChange={(patch) => setJob((prev) => ({ ...prev, ...patch }))}
          />

          {/* Action Buttons - Stacked on mobile, inline on desktop */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
            {/* Add to Calendar Button */}
            <button
              type="button"
              onClick={handleAddToCalendar}
              className="w-full sm:w-auto btn flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              title="Schedule an appointment for this job"
            >
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Add to Calendar
            </button>

            {/* Show Events Popover */}
            <div className="w-full sm:w-auto">
              <CalendarEventsPopover
                calendarEvents={calendarEvents}
                jobId={jobId}
              />
            </div>

            {/* Show Invoices Popover */}
            <div className="w-full sm:w-auto">
              <InvoicesPopover
                invoices={invoices}
                jobId={jobId}
                customerId={customerId}
                customerName={customer.name}
              />
            </div>

            {/* To Do List Toggle Button */}
            <button
              type="button"
              onClick={handleToggleTodoList}
              className={`w-full sm:w-auto btn flex items-center justify-center px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium ${
                customer.addToTodoList
                  ? "bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-400 border border-gray-300"
              }`}
              title={
                customer.addToTodoList
                  ? "Remove from To Do list"
                  : "Add to To Do list"
              }
            >
              {customer.addToTodoList ? (
                <>
                  <svg
                    className="w-5 h-5 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  On To Do List
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  Add to To Do
                </>
              )}
            </button>

            {/* Add Measurement Button - Changed from "Add Area" */}
            <button
              type="button"
              onClick={() => editRoom()}
              className="w-full sm:w-auto btn flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              title="Add a new measurement area"
            >
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Measurement
            </button>

            {/* Add Invoice Button */}
            <button
              type="button"
              onClick={() =>
                navigate(`/invoices/add?customer=${customerId}&job=${jobId}`)
              }
              className="w-full sm:w-auto btn flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              title="Create a new invoice for this job"
            >
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Add Invoice
            </button>
          </div>
        </div>

        {/* Rooms/Areas Table */}
        <RoomsTable
          rooms={roomsList} // You'll need to change this to roomsList since we removed search
          onEditRoom={editRoom}
          onDeleteRoom={deleteRoom}
          customerId={customerId}
          jobId={jobId}
        />
      </div>
      <div className="mt-6">
        <JobDetailsCard
          job={job}
          onJobUpdate={handleJobUpdate}
          customerId={customerId}
          editTrigger={jobEditTrigger} // Add this line
        />
      </div>
      {/* ==========================================
          MODAL DIALOGS - Clean overlay interfaces
          ========================================== */}
      {/* Room Add/Edit Modal */}
      {/* Comprehensive form for room management with validation */}
      <RoomModal
        isOpen={addRoomModal}
        onClose={() => {
          setAddRoomModal(false);
          setRoomParams(JSON.parse(JSON.stringify(defaultRoomParams)));
        }}
        params={roomParams}
        onInputChange={handleRoomInputChange}
        onSave={saveRoom}
      />
      <JobModal
        isOpen={isJobEditModalOpen}
        onClose={() => {
          setIsJobEditModalOpen(false);
        }}
        params={jobParams}
        onInputChange={handleJobInputChange}
        onSave={saveJobFromModal}
      />
      {/* Calendar Event Modal */}
      {/* Professional calendar interface for appointment scheduling */}
      <CalendarEventModal
        isOpen={addCalendarModal}
        onClose={() => {
          setAddCalendarModal(false);
          setCalendarParams({ start: "", end: "", description: "" });
          setEventType("");
          setMinStartDate(getTodayAt(8, 0));
          setMinEndDate(getTodayAt(9, 0));
        }}
        calendarParams={calendarParams}
        onCalendarParamChange={handleCalendarParamChange}
        eventType={eventType}
        onEventTypeChange={handleEventTypeChange}
        onSave={saveCalendarEvent}
        minStartDate={minStartDate}
        minEndDate={minEndDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
      />
    </div>
  );
};

export default ViewCustomerRooms;
